import { useTranslation } from 'react-i18next'
import { BsCalendar2Week } from 'react-icons/bs'
import {
  LuBell,
  LuBookmark,
  LuBuilding2,
  LuCreditCard,
  LuFileText,
  LuHouse,
  LuIdCard,
  LuImage,
  LuLayoutDashboard,
  LuNetwork,
  LuPackage,
  LuPalette,
  LuSettings,
  LuShieldCheck,
  LuSlidersHorizontal,
  LuUsers,
  LuWebhook,
} from 'react-icons/lu'

import { useAuth } from '@/auth/useAuth'
import { SoonBadge } from '@/components/atoms/Badge/SoonBadge'
import { useWhiteLabelFeature } from '@/hooks/featureFlags'
import {
  CommunitiesRoutes,
  NetworksRoutes,
  PoolingRoutes,
  UserRoutes,
} from '@/routes/_hooks/routes.enum'
import { generatePath, useLocation } from '@/routing'

import type { NavRail, NavSection } from './nav.types'
import { resolveNavMode, resolveNavSpace } from './navUtils'

/**
 * Slug → display name, for the objects this person belongs to.
 *
 * The URL carries a slug only, and a rail labelled "getigne-collectif" instead of "Gétigné
 * Collectif" would be a regression in the one place that names what is being configured. The
 * names ride along on the session user, so this costs nothing.
 */
const useObjectNames = (): Map<string, string> => {
  const { user } = useAuth()
  const names = new Map<string, string>()

  for (const edge of user?.networkMemberships?.edges ?? []) {
    const network = edge?.node?.network
    if (network?.slug && network.name) names.set(network.slug, network.name)
  }

  for (const edge of user?.circleMemberships?.edges ?? []) {
    const circle = edge?.node?.circle
    if (circle?.slug && circle.name) names.set(circle.slug, circle.name)
  }

  return names
}

/**
 * The current space's name and root, for whoever needs to say where we are.
 *
 * The rail uses it for the way out of the settings mode; the breadcrumb uses it for its first
 * crumb. Sharing it is what keeps the two from disagreeing — the old breadcrumb read a
 * `groupLabel` carried by a nav model the rail no longer builds.
 */
export const useSpaceIdentity = (): { label: string; to: string } => {
  const { t } = useTranslation()
  const location = useLocation()
  const names = useObjectNames()
  const space = resolveNavSpace(location.pathname)

  if (space.kind === 'network' && space.slug) {
    return {
      label: names.get(space.slug) ?? t('hub.group.network'),
      to: generatePath(NetworksRoutes.Dashboard, { slug: space.slug }),
    }
  }
  if (space.kind === 'community' && space.slug) {
    return {
      label: names.get(space.slug) ?? t('hub.group.community'),
      to: generatePath(CommunitiesRoutes.Dashboard, { slug: space.slug }),
    }
  }

  return { label: t('hub.group.account'), to: '/' }
}

/**
 * The rail for wherever the person is standing.
 *
 * Two modes, never stacked. In `work` the rail carries the object's own surfaces — dashboard,
 * its people, what it pools — and a single door at the foot. In `settings` the door has been
 * taken and the rail is *replaced*: the object's settings, flat, with a way back out.
 *
 * The space is read from the URL, not from the stored context: the `(workspace)` route groups
 * never align `contextIri`, so a deep link to another network's materials would otherwise
 * render the wrong rail. The stored context only answers for `/my`, where the URL names no
 * object.
 */
export const useContextRail = ({ forMenu = false } = {}): NavRail => {
  const { t } = useTranslation()
  const location = useLocation()
  const whiteLabelEnabled = useWhiteLabelFeature()
  const names = useObjectNames()

  const space = resolveNavSpace(location.pathname)
  const mode = resolveNavMode(location.pathname)

  const networkSettings = (slug: string): NavSection => ({
    key: `network:${slug}`,
    label: names.get(slug) ?? t('nav.settings.networkGroup'),
    entries: [
      {
        key: `n-${slug}-general`,
        label: t('hub.nav.general'),
        icon: LuSettings,
        to: generatePath(NetworksRoutes.Edit, { slug }),
      },
      {
        key: `n-${slug}-sso`,
        label: t('nav.settings.sso'),
        icon: LuShieldCheck,
        to: generatePath(NetworksRoutes.SettingsSso, { slug }),
      },
      {
        key: `n-${slug}-webhooks`,
        label: t('hub.nav.webhooks'),
        icon: LuWebhook,
        to: generatePath(NetworksRoutes.SettingsWebhooks, { slug }),
      },
      {
        key: `n-${slug}-whiteLabel`,
        label: t('hub.nav.whiteLabel'),
        icon: LuPalette,
        to: generatePath(NetworksRoutes.SettingsWhiteLabel, { slug }),
        ...(whiteLabelEnabled ? {} : { disabled: true, badge: <SoonBadge /> }),
      },
      {
        key: `n-${slug}-subscription`,
        label: t('hub.nav.subscription'),
        icon: LuCreditCard,
        to: generatePath(NetworksRoutes.SettingsSubscription, { slug }),
      },
    ],
  })

  // A community has no webhooks and no subscription: the design's mock shows both, the API
  // knows neither (`WebhookEndpoint.scope` is Global or Network). The rail lists what exists.
  const communitySettings = (slug: string): NavSection => ({
    key: `circle:${slug}`,
    label: names.get(slug) ?? t('nav.settings.communityGroup'),
    entries: [
      {
        key: `c-${slug}-general`,
        label: t('hub.nav.general'),
        icon: LuSettings,
        to: generatePath(CommunitiesRoutes.Edit, { slug }),
      },
      {
        key: `c-${slug}-media`,
        label: t('hub.nav.media'),
        icon: LuImage,
        to: generatePath(CommunitiesRoutes.SettingsMedia, { slug }),
      },
      {
        key: `c-${slug}-insurance`,
        label: t('hub.nav.insurance'),
        icon: LuShieldCheck,
        to: generatePath(CommunitiesRoutes.SettingsInsurance, { slug }),
      },
      {
        key: `c-${slug}-bookingDocuments`,
        label: t('hub.nav.bookingDocuments'),
        icon: LuFileText,
        to: generatePath(CommunitiesRoutes.SettingsBookingDocuments, { slug }),
      },
    ],
  })

  const accountSettings: NavSection = {
    key: 'account',
    label: t('hub.group.account'),
    entries: [
      {
        key: 'profile',
        label: t('hub.nav.profile'),
        icon: LuIdCard,
        to: UserRoutes.MyProfile,
      },
      {
        key: 'preferences',
        label: t('hub.nav.preferences'),
        icon: LuSlidersHorizontal,
        to: UserRoutes.MyPreferences,
      },
    ],
  }

  // Only the object one came from. Listing every administered object made the settings rail a
  // directory of other people's settings — the way to a different network is the context
  // switcher at the rail's head, which is what it is for.
  const settingsOf = (of: Space): NavSection =>
    of.kind === 'network' && of.slug
      ? networkSettings(of.slug)
      : of.kind === 'community' && of.slug
        ? communitySettings(of.slug)
        : accountSettings

  // The mode is the rail's business, not the menu's. A menu that swapped its own rows on the
  // settings screens would be back to changing shape under the cursor, which is the thing being
  // fixed — and it would lose the door, so the settings of the object one is *currently
  // configuring* would be the one place the submenu could not be opened from.
  if (mode === 'settings' && !forMenu) {
    return {
      sections: [settingsOf(space)],
      backLink: backOutOfSettings(t, space),
    }
  }

  const door = {
    key: 'settings',
    label: t('nav.door.settings'),
    icon: LuSettings,
    to: settingsEntry(space),
    mode: 'settings' as const,
    behind: settingsOf(space),
  }

  return {
    // The user menu asks for the personal space's rows whatever object one is standing in, so
    // that the menu is one fixed list rather than a second, quieter context switcher. Its door
    // still points at the current object: the question "where am I configuring" has a different
    // answer from "where do I keep my own things".
    sections: workSections(t, forMenu ? { kind: 'user' } : space),
    door,
  }
}

/**
 * The rail as the user menu wants it: fixed rows, and the current object's settings behind the
 * door.
 *
 * The menu used to be the rail exactly, which made it change shape with every navigation — the
 * rows moved under the cursor as one crossed from a network to a community, and the one thing a
 * menu in a fixed position should offer is a fixed list. What legitimately follows the context is
 * *Réglages*, because settings are always the settings of something.
 */
export const useUserMenuRail = (): NavRail => useContextRail({ forMenu: true })

type Space = ReturnType<typeof resolveNavSpace>
type Translate = ReturnType<typeof useTranslation>['t']

/** Where the settings door leads: the object's first settings screen. */
const settingsEntry = (space: Space): string => {
  if (space.kind === 'network' && space.slug) {
    return generatePath(NetworksRoutes.Edit, { slug: space.slug })
  }
  if (space.kind === 'community' && space.slug) {
    return generatePath(CommunitiesRoutes.Edit, { slug: space.slug })
  }

  return UserRoutes.MyProfile
}

const backOutOfSettings = (t: Translate, space: Space) => {
  const to =
    space.kind === 'network' && space.slug
      ? generatePath(NetworksRoutes.Dashboard, { slug: space.slug })
      : space.kind === 'community' && space.slug
        ? generatePath(CommunitiesRoutes.Dashboard, { slug: space.slug })
        : PoolingRoutes.MyMaterials

  // Just "Retour". The arrow says the direction and the head says the object; naming it a
  // third time only made the card wrap.
  return { label: t('nav.back.label'), to }
}

/**
 * The work rail, per space.
 *
 * The network and community keep the same shape — dashboard, the people and parts that make
 * the object, then what it pools — so moving between the two teaches nothing new. The user
 * space is a space too, which is what finally gives `/my/lists` and `/my/alerts` a place: both
 * have had real pages and no entry anywhere in the navigation.
 */
const workSections = (t: Translate, space: Space): NavSection[] => {
  if (space.kind === 'network' && space.slug) {
    const slug = space.slug

    return [
      {
        key: 'main',
        entries: [
          {
            key: 'dashboard',
            label: t('hub.nav.overview'),
            icon: LuLayoutDashboard,
            to: generatePath(NetworksRoutes.Dashboard, { slug }),
          },
        ],
      },
      {
        key: 'object',
        label: t('hub.group.network'),
        entries: [
          {
            key: 'communities',
            label: t('menu.network.communities'),
            icon: LuBuilding2,
            to: generatePath(NetworksRoutes.Communities, { slug }),
          },
          {
            key: 'members',
            label: t('hub.nav.members'),
            icon: LuUsers,
            to: generatePath(NetworksRoutes.Members, { slug }),
          },
        ],
      },
      {
        key: 'pooling',
        label: t('nav.group.pooling'),
        entries: [
          {
            key: 'materials',
            label: t('menu.my.materials', { context: 'network' }),
            icon: LuPackage,
            to: generatePath(NetworksRoutes.Materials, { slug }),
          },
          {
            key: 'bookings',
            label: t('menu.my.bookings', { context: 'network' }),
            icon: BsCalendar2Week,
            to: generatePath(NetworksRoutes.BookingAsOwner, { slug }),
          },
        ],
      },
    ]
  }

  if (space.kind === 'community' && space.slug) {
    const slug = space.slug

    return [
      {
        key: 'main',
        entries: [
          {
            key: 'dashboard',
            label: t('hub.nav.overview'),
            icon: LuLayoutDashboard,
            to: generatePath(CommunitiesRoutes.Dashboard, { slug }),
          },
        ],
      },
      {
        key: 'object',
        label: t('hub.group.community'),
        entries: [
          {
            key: 'members',
            label: t('hub.nav.members'),
            icon: LuUsers,
            to: generatePath(CommunitiesRoutes.Members, { slug }),
          },
          // This page exists and has never appeared in any menu — it was reachable only
          // through the rail's contextual path helper.
          {
            key: 'networks',
            label: t('menu.my.networks', { context: 'circle' }),
            icon: LuNetwork,
            to: generatePath(CommunitiesRoutes.Networks, { slug }),
          },
        ],
      },
      {
        key: 'pooling',
        label: t('nav.group.pooling'),
        entries: [
          {
            key: 'materials',
            label: t('menu.my.materials', { context: 'circle' }),
            icon: LuPackage,
            to: generatePath(CommunitiesRoutes.Materials, { slug }),
          },
          {
            key: 'bookings',
            label: t('menu.my.bookings', { context: 'circle' }),
            icon: BsCalendar2Week,
            to: generatePath(CommunitiesRoutes.BookingAsOwner, { slug }),
          },
        ],
      },
    ]
  }

  return [
    {
      key: 'main',
      entries: [
        {
          key: 'home',
          label: t('nav.user.home'),
          icon: LuHouse,
          to: '/',
        },
      ],
    },
    {
      key: 'pooling',
      label: t('nav.group.pooling'),
      entries: [
        {
          key: 'materials',
          label: t('menu.my.materials'),
          icon: LuPackage,
          to: PoolingRoutes.MyMaterials,
        },
        {
          key: 'bookings',
          label: t('menu.my.bookings'),
          icon: BsCalendar2Week,
          to: PoolingRoutes.MyBookingAsOwner,
        },
        {
          key: 'alerts',
          label: t('menu.my.alerts'),
          icon: LuBell,
          to: PoolingRoutes.MyAlerts,
        },
        // Real pages, reachable until now only by typing the URL.
        {
          key: 'lists',
          label: t('nav.user.lists'),
          icon: LuBookmark,
          to: UserRoutes.MyLists,
        },
      ],
    },
    {
      key: 'spaces',
      label: t('nav.group.spaces'),
      entries: [
        {
          key: 'communities',
          label: t('menu.my.communities'),
          icon: LuBuilding2,
          to: CommunitiesRoutes.MyCommunities,
        },
        {
          key: 'networks',
          label: t('menu.my.networks'),
          icon: LuNetwork,
          to: NetworksRoutes.MyNetworks,
        },
      ],
    },
  ]
}
